import { lazy, Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FullPageLoader } from "@/shared/components/Loading/Loading";
import { ROLES } from "../../api/user";
import { SESSION_ENDED_REASON } from "../lib/sessionMessages";

const TosReacceptanceModal = lazy(() => import("@/shared/components/ToS/TosReacceptanceModal"));

const ProtectedRoute = () => {
	const { isAuthenticated, isLoading, user, sessionEnded } = useAuth();
	const location = useLocation();

	// Show loading while checking authentication status
	if (isLoading) {
		return <FullPageLoader />;
	}

	if (!isAuthenticated) {
		const returnTo = location.pathname + location.search;
		const params = new URLSearchParams();
		if (returnTo && returnTo !== "/") params.set("returnTo", returnTo);
		// Carry the reason here as well as in AuthContext's own redirect: AppRoutes
		// keeps the outgoing route tree mounted for its exit animation, and that
		// tree's guard can issue the redirect that ends up in the address bar.
		if (sessionEnded) params.set("reason", SESSION_ENDED_REASON);
		const query = params.toString();
		return <Navigate to={query ? `/login?${query}` : "/login"} replace />;
	}

	// Check if user is a client and restrict access to certain routes
	const isClient = user?.roles.some((role) => [ROLES.CLIENT, ROLES.USER, ROLES.CREATOR].includes(role));
	const restrictedPaths = [
		"/tiktok/similar-creator",
		"/tiktok/ttcm/discover-creator",
		"/tiktok/tto/discover-creator",
		"/data-dashboard",
	];

	if (isClient && restrictedPaths.some((path) => location.pathname.startsWith(path))) {
		return <Navigate to="/campaigns" replace />;
	}

	// When the user must (re-)accept the current Terms of Service, render ONLY
	// the blocking modal — not the routed page. Rendering the <Outlet /> behind
	// the modal would let page components mount, fire effects/API calls, and
	// mutate state despite the "must accept before continuing" requirement.
	const tosAcceptanceRequired = !!user?.tosAcceptanceRequired;
	if (tosAcceptanceRequired) {
		return (
			<Suspense fallback={<FullPageLoader />}>
				<TosReacceptanceModal open />
			</Suspense>
		);
	}

	return <Outlet />;
};

export default ProtectedRoute;
